import React, { useEffect, useState } from 'react';

import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { api } from '../../services/api';
import branding from '../../config/branding';

import {
  Badge,
  BaseText,
  ChecklistCard,
  ChecklistGrid,
  ChecklistItem,
  ChecklistList,
  ChecklistTitle,
  Container,
  ErrorText,
  Field,
  Form,
  InfoCard,
  InfoCardText,
  InfoCardTitle,
  Lead,
  Panel,
  RadioGroup,
  RadioOption,
  Step,
  StepNumber,
  StepsGrid,
  StepText,
  StepTitle,
  SubmitButton,
  SuccessBox,
  Title,
  WarningCallout,
  WarningText,
  WarningTitle,
} from './styles';

const DeleteAccount: React.FC = () => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    setSending(true);
    setError(false);

    try {
      await api.post('account-deletion-requests', {
        email: formData.get('email'),
        request_type: formData.get('request_type'),
        reason: formData.get('reason') || undefined,
      });

      setSent(true);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Header />
      <Container>
        <Badge>
          <span />
          Privacidade e dados pessoais
        </Badge>
        <Title>Solicitação de exclusão de conta</Title>
        <Lead>
          Você pode pedir a exclusão da sua conta TrenzoX e dos dados pessoais
          associados a ela a qualquer momento, direto por esta página. O
          processo é gratuito e não exige contato por telefone.
        </Lead>

        <InfoCard>
          <InfoCardTitle>Como funciona</InfoCardTitle>
          <InfoCardText>
            Três etapas, sem burocracia. Você acompanha tudo pelo e-mail
            cadastrado.
          </InfoCardText>
          <StepsGrid>
            <Step>
              <StepNumber>01</StepNumber>
              <StepTitle>Você envia o pedido</StepTitle>
              <StepText>
                Preencha o formulário abaixo com o e-mail usado na sua conta
                TrenzoX.
              </StepText>
            </Step>
            <Step>
              <StepNumber>02</StepNumber>
              <StepTitle>Conferimos manualmente</StepTitle>
              <StepText>
                Nosso time analisa o pedido e confirma sua identidade pelo
                e-mail cadastrado.
              </StepText>
            </Step>
            <Step>
              <StepNumber>03</StepNumber>
              <StepTitle>Excluímos seus dados</StepTitle>
              <StepText>
                Concluímos a exclusão em até 15 dias úteis e avisamos por e-mail
                quando terminar.
              </StepText>
            </Step>
          </StepsGrid>
        </InfoCard>

        <ChecklistGrid>
          <ChecklistCard>
            <ChecklistTitle>Dados excluídos definitivamente</ChecklistTitle>
            <ChecklistList>
              <ChecklistItem>
                <span>✓</span>
                <span>
                  Dados cadastrais: nome, e-mail, telefone e foto de perfil
                </span>
              </ChecklistItem>
              <ChecklistItem>
                <span>✓</span>
                <span>Histórico de treinos, cargas, séries e frequência</span>
              </ChecklistItem>
              <ChecklistItem>
                <span>✓</span>
                <span>Métricas corporais e registros de evolução</span>
              </ChecklistItem>
              <ChecklistItem>
                <span>✓</span>
                <span>Mensagens do chat com a academia e instrutores</span>
              </ChecklistItem>
              <ChecklistItem>
                <span>✓</span>
                <span>Insígnias, ranking e preferências do aplicativo</span>
              </ChecklistItem>
              <ChecklistItem>
                <span>✓</span>
                <span>Vínculos com academias e tokens de notificação push</span>
              </ChecklistItem>
            </ChecklistList>
          </ChecklistCard>

          <ChecklistCard muted>
            <ChecklistTitle>Dados retidos por obrigação legal</ChecklistTitle>
            <ChecklistList>
              <ChecklistItem muted>
                <span>—</span>
                <span>
                  Registros fiscais e financeiros de assinatura/pagamento,
                  mantidos pelo prazo exigido pela legislação tributária
                </span>
              </ChecklistItem>
              <ChecklistItem muted>
                <span>—</span>
                <span>
                  Dados anonimizados e agregados, que não permitem identificar
                  você
                </span>
              </ChecklistItem>
            </ChecklistList>
          </ChecklistCard>
        </ChecklistGrid>

        <WarningCallout>
          <WarningTitle>A exclusão é irreversível</WarningTitle>
          <WarningText>
            Não é possível recuperar treinos, histórico ou evolução depois que o
            processo é concluído. Se você é gestor de uma academia, transfira a
            titularidade da conta antes de solicitar a exclusão para não
            interromper o acesso dos seus alunos.
          </WarningText>
        </WarningCallout>

        <InfoCard>
          <InfoCardTitle>Formulário de solicitação</InfoCardTitle>
          <InfoCardText>
            Use o mesmo e-mail cadastrado no aplicativo. Solicitações enviadas
            de outro endereço não podem ser validadas.
          </InfoCardText>

          <Panel>
            {sent ? (
              <SuccessBox>
                <div>Solicitação recebida</div>
                <p>
                  Enviamos um e-mail de confirmação pro endereço informado.
                  Vamos analisar e processar seu pedido em breve.
                </p>
              </SuccessBox>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Field>
                  E-mail da conta
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="voce@exemplo.com"
                  />
                </Field>

                <RadioGroup role="radiogroup">
                  <RadioOption>
                    <input
                      type="radio"
                      name="request_type"
                      value="account"
                      defaultChecked
                      required
                    />
                    <div>
                      <strong>Excluir minha conta e todos os dados</strong>
                      <span>
                        Remove sua conta e todos os dados associados a ela
                        (perfil, treinos, medidas, mensagens etc.).
                      </span>
                    </div>
                  </RadioOption>
                  <RadioOption>
                    <input type="radio" name="request_type" value="data_only" />
                    <div>
                      <strong>
                        Excluir apenas meus dados, mantendo a conta
                      </strong>
                      <span>
                        Remove seus dados pessoais e de uso, mas mantém a conta
                        ativa pra você continuar usando a plataforma.
                      </span>
                    </div>
                  </RadioOption>
                </RadioGroup>

                <Field>
                  Motivo (opcional)
                  <textarea
                    name="reason"
                    rows={3}
                    placeholder="Conte pra gente por que está saindo, se quiser."
                  />
                </Field>

                {error && (
                  <ErrorText>
                    Não foi possível enviar sua solicitação agora. Tente
                    novamente em instantes ou entre em contato pelo e-mail{' '}
                    {branding.supportEmail}.
                  </ErrorText>
                )}

                <SubmitButton type="submit" disabled={sending}>
                  {sending ? 'Enviando...' : 'Enviar solicitação'}
                </SubmitButton>
              </Form>
            )}
          </Panel>
        </InfoCard>

        <InfoCard>
          <InfoCardTitle style={{ fontSize: 20, marginBottom: 0 }}>
            Prefere enviar por e-mail?
          </InfoCardTitle>
          <BaseText>
            Escreva para{' '}
            <a href={`mailto:${branding.supportEmail}`}>
              {branding.supportEmail}
            </a>{' '}
            a partir do e-mail cadastrado, com o assunto “Exclusão de conta”. O
            prazo de atendimento é o mesmo do formulário.
          </BaseText>
        </InfoCard>
      </Container>
      <Footer />
    </>
  );
};

export { DeleteAccount };
